import { Modal } from './index';
import { T } from '../../translation/src';
import React, { memo } from 'react';
import { Tab, TabGroup, TabList, TabPanel } from '@headlessui/react';
import { Icon } from '../Icon';
import { LinkIcon } from '@heroicons/react/24/outline';
import { RendererToolType, useRendererTools } from '../../hooks/potree/useRenderer';
import { useViewManager } from '../../hooks/useViewManager';

type Group = 'polygon' | 'point' | 'line' | 'floorplan' | 'surface' | 'navigation' | 'magic wand' | 'box' | 'screenshot';

const videoLinks = {
  floorplanResidentialHouse: 'https://www.youtube.com/embed/ssO9-b2yYYc?si=OPo-FqhjXgJa2eZA',
  floorplanCrossSectionFacade: 'https://www.youtube.com/embed/4gDU0fPmcic?si=i3UH2Zc0e2LuZ2w1',
  floorplanWindows: 'https://www.youtube.com/embed/Dxb-q8BaIwo?si=esLgt6GfiZOSsHXe',
  floorplanElevation: 'https://www.youtube.com/embed/epR9XaaL7VE?si=fxBZgKrGikJqQgWY',
  volumesAndProfiles: 'https://www.youtube.com/embed/7D0Hnetnsfg?si=uLdylPG923YWRnwy',
  annotation: 'https://www.youtube.com/embed/h4-w1Zk9JoY?si=X1uDssrdhPvehOyd',
  lassoTool: 'https://www.youtube.com/embed/mZdCh12r5Pc?si=MoBLyIxw2k1jy-dH',
  objectRecognitionAndClassification: 'https://www.youtube.com/embed/8bsSVeSTjN0?si=_z0NWt7fG0BbxtHw',
  streamlineMEPWorkflow: 'https://www.youtube.com/embed/HKJ-VsUUhbQ?si=ReqdYq8n44WC_oW6',
  basicNavigation: 'https://www.youtube.com/embed/KcbgnfnP6AQ?si=Nmm_3tLEaJy-oxe_',
  planesPlacing: 'https://www.youtube.com/embed/jyUV2_zxVbE?si=7T36LKiKMt460LKG',
  floorplanCreating: 'https://www.youtube.com/embed/-oUDHQLpLuI?si=lQ-NAdbtOZfs-Mbf',
  MeasuringLineTool: 'https://www.youtube.com/embed/v7x3Bfrpotc?si=ELzQp6_y9cpmRBTY',
  MeasuringLineAndPlane: 'https://www.youtube.com/embed/xCa5drdn7TQ?si=SoIHTxehBSN-aHdK',
  MeasuringLineAnd2Planes: 'https://www.youtube.com/embed/Rz0DIL9293M?si=U62IPhkGO0wJCDf2',
  Exporting2DScreenshot: 'https://www.youtube.com/embed/_7VRmBC6-Ck?si=NXhtawyk0MQW6jFc',
};

type VideoLinksByMode = Record<'2D' | '3D', string[]>;

// Creates video configuration for 2D and 3D modes. If threeDVideos is not provided, uses the same videos for both modes.
const createModeVideos = (vids2D: string[], vids3D: string[] = vids2D): VideoLinksByMode => ({
  '2D': vids2D,
  '3D': vids3D,
});

const videoLinksByGroup: Record<Group, VideoLinksByMode> = {
  floorplan: createModeVideos([], [videoLinks.floorplanResidentialHouse, videoLinks.floorplanCrossSectionFacade, videoLinks.floorplanWindows, videoLinks.floorplanElevation, videoLinks.floorplanCreating]),
  polygon: createModeVideos([videoLinks.volumesAndProfiles, videoLinks.annotation]),
  point: createModeVideos([videoLinks.annotation]),
  line: createModeVideos([videoLinks.annotation, videoLinks.MeasuringLineTool, videoLinks.MeasuringLineAndPlane, videoLinks.MeasuringLineAnd2Planes]),
  surface: createModeVideos(
    [],
    [videoLinks.floorplanResidentialHouse, videoLinks.floorplanCrossSectionFacade, videoLinks.floorplanWindows, videoLinks.floorplanElevation, videoLinks.planesPlacing, videoLinks.MeasuringLineAndPlane, videoLinks.MeasuringLineAnd2Planes],
  ),
  navigation: createModeVideos([videoLinks.basicNavigation]),
  'magic wand': createModeVideos([videoLinks.lassoTool, videoLinks.objectRecognitionAndClassification, videoLinks.annotation, videoLinks.streamlineMEPWorkflow]),
  box: createModeVideos([]),
  screenshot: createModeVideos([videoLinks.Exporting2DScreenshot], []),
};

const selectedToolToGroup = (selectedTool: RendererToolType): Group | undefined => {
  switch (selectedTool) {
    case RendererToolType.POINT:
      return 'point';
    case RendererToolType.DISTANCE:
      return 'line';
    case RendererToolType.AREA:
      return 'polygon';
    case RendererToolType.SURFACE:
      return 'surface';
    case RendererToolType.FLOORPLAN:
      return 'floorplan';
    case RendererToolType.CLUSTER:
      return 'magic wand';
    case RendererToolType.SELECT:
      return 'navigation';
    case RendererToolType.BOX:
      return 'box';
    case RendererToolType.SCREENSHOT:
      return 'screenshot';
  }
};

const useGroupTranslations = (): Record<Group, React.JSX.Element> => ({
  polygon: <T swc _str="polygon" />,
  point: <T swc _str="point" />,
  line: <T swc _str="line" />,
  floorplan: <T swc _str="floorplan" />,
  surface: <T swc _str="surface" />,
  navigation: <T swc _str="navigation" />,
  'magic wand': <T swc _str="magic wand" />,
  box: <T swc _str="box" />,
  screenshot: <T swc _str="screenshot" />,
});

type ShortcutsListItem = {
  group: Group;
  shortcuts: {
    description: string | React.JSX.Element;
    shortcuts: (string | React.JSX.Element)[];
  }[];
};

const ShortcutsList: React.FC2<ShortcutsListItem> = ({ group, shortcuts }) => {
  const groupTranslations = useGroupTranslations();
  return (
    <div>
      <h3 className="text-lg font-bold">{groupTranslations[group]}</h3>

      {shortcuts.map((shortcut, index) => (
        <div key={index} className="flex justify-between space-y-2">
          <span>{shortcut.description}</span>
          <div className="flex gap-2">
            {shortcut.shortcuts.map((shortcutKey, index) => (
              <div key={index} className="px-2 py-px text-xs bg-gray-100 rounded dark:bg-gray-700">
                {shortcutKey}
              </div>
            ))}
          </div>
        </div>
      ))}
    </div>
  );
};

interface ShortcutsModalProps {
  open: boolean;
  onClose: () => void;
  shortcuts: ShortcutsListItem[];
}
const ShortcutsModal_: React.FC2<ShortcutsModalProps> = ({ open, onClose, shortcuts }) => {
  const [{ selectedTool }] = useRendererTools();
  const currentGroup = selectedToolToGroup(selectedTool);

  const { in2DMode } = useViewManager();

  const currentShortcuts = currentGroup && shortcuts.find((group) => group.group === currentGroup);
  const currentVideoLinks = currentGroup && videoLinksByGroup[currentGroup][in2DMode ? '2D' : '3D'];

  const onNavigateToVideos = (event: React.MouseEvent<HTMLButtonElement>) => {
    event.preventDefault();
    event.stopPropagation();
    window.open('https://youtube.com/playlist?list=PLyPWFTmkdeAhnUXWslHgHE06RVrqH8keE&si=GhBGVRtCx4E4Pb1A', '_blank');
  };

  return (
    <TabGroup defaultIndex={currentGroup ? 0 : 1} key={currentGroup ? 0 : 1}>
      <Modal.Container open={open} onClose={onClose} panelClassName="min-w-[70rem]">
        <Modal.Header className="w-full">
          <T _str="Keyboard Shortcuts" />
          <TabList className="flex gap-4 ml-auto">
            <Tab
              name="current tool"
              className="rounded-full py-1 px-3 text-sm/6 font-semibold text-white focus:outline-none data-[selected]:bg-white/10 data-[hover]:bg-white/5 data-[selected]:data-[hover]:bg-white/10 data-[focus]:outline-1 data-[focus]:outline-white"
            >
              <T swc _str="current tool" />
            </Tab>
            <Tab
              name="all shortcuts"
              className="rounded-full py-1 px-3 text-sm/6 font-semibold text-white focus:outline-none data-[selected]:bg-white/10 data-[hover]:bg-white/5 data-[selected]:data-[hover]:bg-white/10 data-[focus]:outline-1 data-[focus]:outline-white"
            >
              <T swc _str="all shortcuts" />
            </Tab>
            <Tab
              onClick={onNavigateToVideos}
              name="current tool"
              className="rounded-full py-1 px-3 text-sm/6 font-semibold text-white focus:outline-none data-[selected]:bg-white/10 data-[hover]:bg-white/5 data-[selected]:data-[hover]:bg-white/10 data-[focus]:outline-1 data-[focus]:outline-white"
            >
              <div className="flex items-center gap-2">
                <Icon icon={LinkIcon} className="w-4 h-4" />
                <T swc _str="all videos" />
              </div>
            </Tab>
          </TabList>
        </Modal.Header>
        <Modal.Body className="w-full">
          <TabPanel key="current tool">
            <div className="grid grid-cols-2 gap-8 overflow-auto h-96">
              <div>
                {currentShortcuts?.shortcuts?.length ? (
                  <ShortcutsList {...currentShortcuts} />
                ) : (
                  <h3 className="text-lg font-bold">
                    <T swc _str="no shortcuts for this tool" />
                  </h3>
                )}
              </div>
              <div className="flex flex-col h-full gap-4 overflow-auto">
                <h3 className="text-lg font-bold">
                  <T swc _str="related videos" />
                </h3>
                <div className="flex flex-col h-full gap-4 overflow-auto">
                  {currentVideoLinks?.length ? (
                    currentVideoLinks.map((link) => (
                      <div key={link}>
                        <iframe
                          src={link}
                          title="video"
                          className="w-full h-64"
                          allow="accelerometer; autoplay; clipboard-write; encrypted-media; gyroscope; picture-in-picture; web-share"
                          referrerPolicy="strict-origin-when-cross-origin"
                          allowFullScreen
                        />
                      </div>
                    ))
                  ) : (
                    <h3 className="text-lg font-bold">
                      <T swc _str="no videos for this tool" />
                    </h3>
                  )}
                </div>
              </div>
            </div>
          </TabPanel>
          <TabPanel key="all shortcuts">
            <div className="grid grid-cols-3 gap-8 overflow-auto h-96">
              {shortcuts.map((group, index) => (
                <ShortcutsList key={index} {...group} />
              ))}
            </div>
          </TabPanel>
        </Modal.Body>
      </Modal.Container>
    </TabGroup>
  );
};

export const ShortcutsModal = memo(ShortcutsModal_);
